import { useState } from 'react';
import {
    Plus,
    FolderKanban,
    Search,
    Archive,
    Loader2,
} from 'lucide-react';
import { useProjects, useDeleteProject, useLeaveProject } from '@/hooks/use-projects';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ProjectCard } from '@/components/projects/project-card';
import { CreateProjectDialog } from '@/components/projects/create-project-dialog';
import { AppLayout } from '@/components/layout/app-layout';

export function ProjectsPage() {
    const { toast } = useToast();
    const { t } = useI18n();
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [leaveConfirm, setLeaveConfirm] = useState<{ projectId: string; memberId: string; projectName: string } | null>(null);

    const { data: projects, isLoading } = useProjects({ archived: showArchived ? true : undefined });
    const deleteProject = useDeleteProject();
    const leaveProject = useLeaveProject();

    const filteredProjects = projects?.filter((project) => {
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                project.name.toLowerCase().includes(query) ||
                project.description?.toLowerCase().includes(query)
            );
        }
        return true;
    });

    const handleArchive = async (projectId: string) => {
        const project = projects?.find((p) => p.id === projectId);
        if (!project) return;

        try {
            const response = await fetch(
                `/api/projects/${projectId}`,
                {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ isArchived: !project.isArchived }),
                }
            );

            if (!response.ok) throw new Error('Failed to update project');

            toast({
                title: project.isArchived ? 'Project unarchived' : 'Project archived',
                description: `"${project.name}" has been ${project.isArchived ? 'unarchived' : 'archived'}.`,
            });

            window.location.reload();
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to update project',
                variant: 'destructive',
            });
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;

        const project = projects?.find((p) => p.id === deleteConfirm);
        if (!project) return;

        try {
            await deleteProject.mutateAsync(deleteConfirm);

            toast({
                title: 'Project deleted',
                description: `"${project.name}" has been permanently deleted.`,
            });

            setDeleteConfirm(null);
        } catch (error) {
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to delete project',
                variant: 'destructive',
            });
        }
    };

    const handleLeave = async () => {
        if (!leaveConfirm) return;

        try {
            await leaveProject.mutateAsync({
                projectId: leaveConfirm.projectId,
                memberId: leaveConfirm.memberId
            });

            toast({
                title: 'Left project',
                description: `You have left "${leaveConfirm.projectName}".`,
            });

            setLeaveConfirm(null);
        } catch (error) {
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to leave project',
                variant: 'destructive',
            });
        }
    };

    return (
        <AppLayout>
            <div className="p-4 sm:p-6 md:p-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-white">{t('nav.projects')}</h1>
                            <p className="text-white/70 mt-1 text-sm sm:text-base">
                                {t('project.manageProjects')}
                            </p>
                        </div>
                        <Button onClick={() => setShowCreateDialog(true)} className="w-full sm:w-auto">
                            <Plus className="h-4 w-4 mr-2" />
                            {t('project.newProject')}
                        </Button>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-6 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                            <Input
                                placeholder={t('project.searchProjects')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <Button
                            variant={showArchived ? 'secondary' : 'outline'}
                            onClick={() => setShowArchived(!showArchived)}
                            className="w-full sm:w-auto"
                        >
                            <Archive className="h-4 w-4 mr-2" />
                            {showArchived ? t('project.showActive') : t('project.showArchived')}
                        </Button>
                    </div>

                    {/* Projects grid */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : filteredProjects?.length === 0 ? (
                        <div className="text-center py-12">
                            <FolderKanban className="h-10 w-10 sm:h-12 sm:w-12 text-foreground-muted mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-foreground mb-2">
                                {searchQuery
                                    ? t('project.noProjectsFound')
                                    : showArchived
                                        ? t('project.noArchivedProjects')
                                        : t('project.noProjects')}
                            </h3>
                            <p className="text-foreground-muted mb-4 text-sm px-4">
                                {searchQuery
                                    ? t('project.tryDifferentSearch')
                                    : showArchived
                                        ? t('project.archiveToSeeHere')
                                        : t('project.createFirstProject')}
                            </p>
                            {!searchQuery && !showArchived && (
                                <Button onClick={() => setShowCreateDialog(true)}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('project.createProject')}
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredProjects?.map((project) => (
                                <ProjectCard
                                    key={project.id}
                                    project={project}
                                    onArchive={handleArchive}
                                    onDelete={(id) => setDeleteConfirm(id)}
                                    onLeave={(projectId, memberId) =>
                                        setLeaveConfirm({ projectId, memberId, projectName: project.name })
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create project dialog */}
            <CreateProjectDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
            />

            {/* Delete confirmation dialog */}
            <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('project.deleteProject')}</DialogTitle>
                        <DialogDescription>
                            {t('project.deleteConfirmation')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="w-full sm:w-auto">
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteProject.isPending}
                            className="w-full sm:w-auto"
                        >
                            {deleteProject.isPending && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            {t('project.deleteProject')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Leave confirmation dialog */}
            <Dialog open={!!leaveConfirm} onOpenChange={() => setLeaveConfirm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('project.leaveProject')}</DialogTitle>
                        <DialogDescription>
                            {t('project.leaveConfirmation')} "{leaveConfirm?.projectName}"?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setLeaveConfirm(null)} className="w-full sm:w-auto">
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleLeave}
                            disabled={leaveProject.isPending}
                            className="w-full sm:w-auto"
                        >
                            {leaveProject.isPending && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            {t('project.leaveProject')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppLayout>
    );
}
